import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logAuthContext } from "@/utils/consoleLogger";
import { getEmailRedirectUrl, logEnvironmentInfo } from "@/utils/environmentRedirects";

// Define timeout durations in milliseconds
const AUTH_OPERATION_TIMEOUT = 15000; // 15 seconds
const SESSION_CHECK_TIMEOUT = 10000; // 10 seconds
const SESSION_REFRESH_TIMEOUT = 10000; // 10 seconds
const RECOVERY_CHECK_INTERVAL = 5000; // 5 seconds

// Define the shape of our AuthContext
interface AuthContextType {
  // Auth state
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isVendor: boolean;
  isVendorLoading: boolean;
  displayName: string;
  
  // Auth operations
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, confirmPassword: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  createOrUpdateProfile: (userId: string, profileData: { display_name: string, email: string }) => Promise<void>;
  
  // Debug information
  authStatus: string;
  lastError: string | null;
  lastOperation: string | null;
  debugLogs: string[];
}

// Create context with a default empty value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
export function AuthProvider({ children }: { children: ReactNode }) {
  // State for authentication
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVendor, setIsVendor] = useState(false);
  const [isVendorLoading, setIsVendorLoading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  
  // State for debugging and monitoring
  const [authStatus, setAuthStatus] = useState<string>("initializing");
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastOperation, setLastOperation] = useState<string | null>(null);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  
  // Refs for timeouts and recovery
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recoveryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const operationTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const authListenerRef = useRef<{ subscription: { unsubscribe: () => void } } | null>(null);
  
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Helper for adding debug logs
  const addLog = (message: string, type: "info" | "error" | "warning" | "success" = "info") => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${type.toUpperCase()}] ${message}`;
    logAuthContext(logMessage);
    setDebugLogs(prev => [...prev.slice(-99), logMessage]);
  };

  // Helper for setting operation timeouts
  const setOperationTimeout = (operationName: string, timeoutMs: number, onTimeout: () => void) => {
    // Clear any existing timeout for this operation
    if (operationTimeoutsRef.current[operationName]) {
      clearTimeout(operationTimeoutsRef.current[operationName]);
    }
    
    // Set new timeout
    operationTimeoutsRef.current[operationName] = setTimeout(() => {
      addLog(`Operation timeout: ${operationName} exceeded ${timeoutMs}ms`, "error");
      onTimeout();
    }, timeoutMs);
    
    return () => {
      if (operationTimeoutsRef.current[operationName]) {
        clearTimeout(operationTimeoutsRef.current[operationName]);
        delete operationTimeoutsRef.current[operationName];
      }
    };
  };

  // Start recovery mode
  const startRecoveryMode = () => {
    if (recoveryIntervalRef.current) {
      clearInterval(recoveryIntervalRef.current);
    }
    
    addLog("Starting recovery mode...", "warning");
    setAuthStatus("recovery");
    
    // Clear all operation timeouts
    Object.keys(operationTimeoutsRef.current).forEach(key => {
      clearTimeout(operationTimeoutsRef.current[key]);
      delete operationTimeoutsRef.current[key];
    });
    
    // Try to recover by clearing state and restarting auth flow
    recoveryIntervalRef.current = setInterval(async () => {
      addLog("Recovery attempt...", "info");
      
      try {
        // Try to get session again
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          addLog(`Recovery got session error: ${error.message}`, "error");
          // Force clear session and try to refresh
          setSession(null);
          setUser(null);
          
          try {
            await supabase.auth.refreshSession();
          } catch (refreshError) {
            addLog(`Recovery refresh failed: ${(refreshError as Error).message}`, "error");
          }
        } else if (data.session) {
          // Successfully recovered
          addLog("Recovery successful! Session found", "success");
          setSession(data.session);
          setUser(data.session.user);
          setAuthStatus("authenticated");
          setIsLoading(false);
          
          // Stop recovery mode
          if (recoveryIntervalRef.current) {
            clearInterval(recoveryIntervalRef.current);
            recoveryIntervalRef.current = null;
          }
        } else {
          // No valid session found, consider user logged out
          addLog("Recovery: No valid session found, setting unauthenticated", "info");
          setSession(null);
          setUser(null);
          setAuthStatus("unauthenticated");
          setIsLoading(false);
          
          // Stop recovery mode
          if (recoveryIntervalRef.current) {
            clearInterval(recoveryIntervalRef.current);
            recoveryIntervalRef.current = null;
          }
          
          // If on a protected route, redirect to login
          if (location.pathname !== "/auth" && 
              !location.pathname.includes("/terms-of-service") && 
              !location.pathname.startsWith("/service/")) {
            addLog(`Recovery redirecting to /auth from ${location.pathname}`, "info");
            navigate("/auth");
          }
        }
      } catch (e) {
        addLog(`Recovery error: ${(e as Error).message}`, "error");
      }
    }, RECOVERY_CHECK_INTERVAL);
  };

  // Check admin status
  const checkAdminStatus = async (userId: string) => {
    try {
      setLastOperation("checkAdminStatus");
      addLog(`Checking admin status for user ${userId}`, "info");
      
      const clearTimeout = setOperationTimeout("checkAdminStatus", AUTH_OPERATION_TIMEOUT, () => {
        setIsAdmin(false);
        addLog("Admin check timed out, defaulting to false", "warning");
      });
      
      const { data: hasAdminRole, error } = await supabase.rpc('has_role', {
        '_role': 'admin'
      });

      clearTimeout();
      
      if (error) {
        addLog(`Error checking admin status: ${error.message}`, "error");
        setIsAdmin(false);
        return;
      }
      
      addLog(`Admin status check result: ${hasAdminRole}`, "info");
      setIsAdmin(hasAdminRole ? true : false);
    } catch (error) {
      addLog(`Exception in checkAdminStatus: ${(error as Error).message}`, "error");
      setIsAdmin(false);
    }
  };

  // Check vendor role - simplified without delays
  const checkVendorRole = async (userId: string) => {
    try {
      setLastOperation("checkVendorRole");
      setIsVendorLoading(true);
      addLog(`Checking vendor role for user ${userId}`, "info");
      
      const clearTimeout = setOperationTimeout("checkVendorRole", AUTH_OPERATION_TIMEOUT, () => {
        setIsVendor(false);
        setIsVendorLoading(false);
        addLog("Vendor check timed out, defaulting to false", "warning");
      });
      
      const { data: hasVendorRole, error } = await supabase.rpc('has_role', {
        '_role': 'vendor'
      });

      clearTimeout();
      
      if (error) {
        addLog(`Error checking vendor role: ${error.message}`, "error");
        setIsVendor(false);
      } else {
        addLog(`Vendor role check result: ${hasVendorRole}`, "info");
        setIsVendor(hasVendorRole ? true : false);
      }
    } catch (error) {
      addLog(`Exception in checkVendorRole: ${(error as Error).message}`, "error");
      setIsVendor(false);
    } finally {
      setIsVendorLoading(false);
    }
  };

  // Fetch user profile
  const fetchUserProfile = async (userId: string) => {
    try {
      setLastOperation("fetchUserProfile");
      addLog(`Fetching profile for user ${userId}`, "info");
      
      const clearTimeout = setOperationTimeout("fetchUserProfile", AUTH_OPERATION_TIMEOUT, () => {
        setDisplayName("");
        addLog("Profile fetch timed out", "warning");
      });
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      clearTimeout();
      
      if (error) {
        addLog(`Error fetching user profile: ${error.message}`, "error");
        return;
      }
      
      if (data && data.display_name) {
        addLog(`Found profile with display_name: ${data.display_name}`, "success");
        setDisplayName(data.display_name);
      } else {
        addLog("No display name found in profile or no profile exists", "warning");
        
        try {
          const { data: userData } = await supabase.auth.getUser();
          
          if (userData && userData.user) {
            const email = userData.user.email;
            // Check if display_name exists in user metadata
            const metadataDisplayName = userData.user.user_metadata?.display_name;
            
            // Use metadata display name if available, otherwise fall back to email prefix
            let defaultName = metadataDisplayName || (email ? email.split('@')[0] : 'User');
            
            // Only add prefix if we're using the fallback and it's too short
            if (!metadataDisplayName && defaultName.length < 3) {
              defaultName = `User-${defaultName}`;
            }
            
            addLog(`Creating profile with display name: ${defaultName}`, "info");
            
            try {
              await createOrUpdateProfile(userId, {
                display_name: defaultName,
                email: email || ""
              });
              
              setDisplayName(defaultName);
            } catch (insertErr) {
              addLog(`Error upserting profile: ${(insertErr as Error).message}`, "error");
            }
          }
        } catch (error) {
          addLog(`Error in fetchUserProfile: ${(error as Error).message}`, "error");
        }
      }
    } catch (error) {
      addLog(`Exception in fetchUserProfile: ${(error as Error).message}`, "error");
    }
  };

  // Initialize auth state
  useEffect(() => {
    let isComponentMounted = true;
    
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthStatus("initializing");
        addLog("Initializing auth state...", "info");
        
        // Set a timeout for the entire auth initialization process
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
        }
        
        authTimeoutRef.current = setTimeout(() => {
          if (isComponentMounted) {
            addLog("Auth initialization timed out! Starting recovery...", "error");
            startRecoveryMode();
          }
        }, AUTH_OPERATION_TIMEOUT);
        
        // 1. FIRST: Set up auth state change listener
        addLog("Setting up auth state change listener", "info");
        const { data: authSubscription } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (!isComponentMounted) return;
          
          addLog(`Auth state changed: ${event}, session: ${newSession ? "exists" : "null"}`, "info");
          
          const isAuthenticated = !!newSession;
          if (newSession) {
            setSession(newSession);
            setUser(newSession.user);
            setAuthStatus("authenticated");
            
            // Perform additional checks immediately - no delays
            checkAdminStatus(newSession.user.id);
            checkVendorRole(newSession.user.id);
            fetchUserProfile(newSession.user.id);
          } else {
            setSession(null);
            setUser(null);
            setIsAdmin(false);
            setIsVendor(false);
            setIsVendorLoading(false);
            setDisplayName("");
            setAuthStatus("unauthenticated");
          }
        });
        
        authListenerRef.current = authSubscription;
        
        // 2. THEN: Check for existing session
        addLog("Checking for existing session", "info");
        const sessionCheckTimeout = setTimeout(() => {
          addLog("Session check timed out", "warning");
          setIsLoading(false);
          setAuthStatus("timeout");
        }, SESSION_CHECK_TIMEOUT);
        
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        clearTimeout(sessionCheckTimeout);
        
        if (sessionError) {
          addLog(`Error fetching session: ${sessionError.message}`, "error");
          setIsLoading(false);
          setAuthStatus("error");
          setLastError(sessionError.message);
          
          // Try to recover by refreshing the session
          try {
            addLog("Attempting session refresh after error", "info");
            const refreshTimeout = setTimeout(() => {
              addLog("Session refresh timed out", "warning");
              setIsLoading(false);
              setAuthStatus("timeout");
            }, SESSION_REFRESH_TIMEOUT);
            
            const { data: refreshResult, error: refreshError } = await supabase.auth.refreshSession();
            
            clearTimeout(refreshTimeout);
            
            if (refreshError) {
              addLog(`Error refreshing session: ${refreshError.message}`, "error");
              setIsLoading(false);
              setAuthStatus("error");
              setLastError(refreshError.message);
            } else if (refreshResult.session) {
              addLog("Session refreshed successfully", "success");
              setSession(refreshResult.session);
              setUser(refreshResult.session.user);
              setAuthStatus("authenticated");
              setIsLoading(false);
              
              // Update other user state
              checkAdminStatus(refreshResult.session.user.id);
              checkVendorRole(refreshResult.session.user.id);
              fetchUserProfile(refreshResult.session.user.id);
            } else {
              addLog("No session after refresh attempt", "warning");
              setIsLoading(false);
              setAuthStatus("unauthenticated");
            }
          } catch (refreshExcept) {
            addLog(`Exception during session refresh: ${(refreshExcept as Error).message}`, "error");
            setIsLoading(false);
            setAuthStatus("error");
            setLastError((refreshExcept as Error).message);
          }
        } else {
          // 3. THEN: Process the session result
          if (sessionData.session) {
            addLog(`Session found for user ${sessionData.session.user.id}`, "success");
            setSession(sessionData.session);
            setUser(sessionData.session.user);
            setAuthStatus("authenticated");
            
            // Update other user state immediately
            checkAdminStatus(sessionData.session.user.id);
            checkVendorRole(sessionData.session.user.id);
            fetchUserProfile(sessionData.session.user.id);
          } else {
            addLog("No active session found", "info");
            setAuthStatus("unauthenticated");
          }
          
          setIsLoading(false);
        }
        
        // Clear the main auth timeout since initialization is complete
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
      } catch (err) {
        if (isComponentMounted) {
          addLog(`Unhandled error in auth initialization: ${(err as Error).message}`, "error");
          setIsLoading(false);
          setAuthStatus("error");
          setLastError((err as Error).message);
          
          // Start recovery mode in case of unhandled errors
          startRecoveryMode();
        }
      }
    };
    
    initializeAuth();
    
    // Cleanup function
    return () => {
      isComponentMounted = false;
      
      // Clear all timeouts
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
      
      if (recoveryIntervalRef.current) {
        clearInterval(recoveryIntervalRef.current);
      }
      
      Object.keys(operationTimeoutsRef.current).forEach(key => {
        clearTimeout(operationTimeoutsRef.current[key]);
      });
      
      // Unsubscribe from auth changes
      if (authListenerRef.current) {
        authListenerRef.current.subscription.unsubscribe();
      }
    };
  }, []);

  // Create or update user profile
  const createOrUpdateProfile = async (userId: string, profileData: { display_name: string, email: string }) => {
    try {
      setLastOperation("createOrUpdateProfile");
      addLog(`Creating/updating profile for user ${userId}`, "info");
      
      const clearTimeout = setOperationTimeout("createOrUpdateProfile", AUTH_OPERATION_TIMEOUT, () => {
        addLog("Profile update timed out", "error");
        setLastError("Profile update operation timed out");
        throw new Error("Profile update operation timed out");
      });
      
      try {
        const { data, error } = await supabase.rpc('create_or_update_profile', {
          user_id: userId,
          user_email: profileData.email,
          user_display_name: profileData.display_name
        });
        
        clearTimeout();
        
        if (error) {
          addLog(`Error from create_or_update_profile RPC: ${error.message}`, "error");
          setLastError(error.message);
          throw error;
        }
        
        addLog("Profile created/updated successfully via RPC", "success");
        setDisplayName(profileData.display_name);
        return;
      } catch (rpcError) {
        addLog(`RPC failed, attempting direct upsert: ${(rpcError as Error).message}`, "warning");
        
        // Prepare update data
        const updateData = {
          id: userId,
          email: profileData.email,
          display_name: profileData.display_name,
          updated_at: new Date().toISOString()
        };
        
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert(updateData, { onConflict: 'id' });
        
        clearTimeout();
        
        if (upsertError) {
          addLog(`Error from direct profile upsert: ${upsertError.message}`, "error");
          setLastError(upsertError.message);
          throw upsertError;
        }
        
        addLog("Profile created/updated successfully via direct upsert", "success");
        setDisplayName(profileData.display_name);
      }
    } catch (error) {
      addLog(`Error in createOrUpdateProfile: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
      throw error;
    }
  };

  // Sign in functionality
  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setLastOperation("signIn");
      setLastError(null);
      setAuthStatus("authenticating");
      addLog(`Sign in attempt for email: ${email}`, "info");
      
      const clearTimeout = setOperationTimeout("signIn", AUTH_OPERATION_TIMEOUT, () => {
        setIsLoading(false);
        setAuthStatus("timeout");
        setLastError("Sign in operation timed out");
        addLog("Sign in timed out", "error");
        toast({
          variant: "destructive",
          title: "Sign In Failed",
          description: "The operation timed out. Please try again.",
        });
      });

      // Check if the input is a phone number or email
      const isPhoneNumber = email.trim().startsWith('+');
      let loginEmail = email.trim();
      
      if (isPhoneNumber) {
        // For phone login, we need to find the associated email
        addLog(`Phone login attempt for: ${email}`, "info");
        
        // First check if a user with this phone exists in our invitations
        const { data: inviteData, error: inviteError } = await supabase
          .from('sms_invitations')
          .select('*')
          .eq('phone_number', email.trim())
          .eq('invitation_status', 'claimed')
          .single();
          
        if (inviteError || !inviteData?.claimed_by_user_id) {
          setIsLoading(false);
          setAuthStatus("error");
          setLastError("Phone number not found or invitation not claimed");
          addLog(`Phone login failed: ${inviteError?.message || 'No claimed invitation found'}`, "error");
          
          toast({
            variant: "destructive",
            title: "Login Failed",
            description: "Phone number not registered or invitation not completed. Please use your email address or complete your SMS invitation.",
          });
          return;
        }
        
        // Get the user's email from their profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', inviteData.claimed_by_user_id)
          .single();
          
        if (profileError || !profileData?.email) {
          setIsLoading(false);
          setAuthStatus("error");
          setLastError("Could not find email for phone number");
          addLog(`Phone login failed: ${profileError?.message || 'No email found'}`, "error");
          
          toast({
            variant: "destructive", 
            title: "Login Failed",
            description: "Could not find email associated with this phone number. Please use your email address.",
          });
          return;
        }
        
        loginEmail = profileData.email;
        addLog(`Phone login resolved to email: ${loginEmail}`, "info");
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: password.trim(),
      });

      clearTimeout();

      if (error) {
        setIsLoading(false);
        setAuthStatus("error");
        setLastError(error.message);
        addLog(`Sign in error: ${error.message}`, "error");
        
        let errorMessage = error.message;
        if (error.message.includes("Email not confirmed")) {
          errorMessage = "Please check your email for the confirmation link before signing in.";
        } else if (error.message.includes("Invalid login credentials")) {
          errorMessage = "Invalid email or password. Please try again.";
        }
        
        toast({
          variant: "destructive",
          title: "Sign In Failed",
          description: errorMessage,
        });
        
        throw error;
      }

      if (data.user) {
        addLog(`Sign in successful for user ${data.user.id}`, "success");
        setAuthStatus("authenticated");
        
        const profileData = { 
          display_name: data.user.user_metadata?.display_name || data.user.email?.split('@')[0] || 'User',
          email: data.user.email || email
        };
        
        await createOrUpdateProfile(data.user.id, profileData);
        navigate('/');
      }
    } catch (error) {
      addLog(`Exception in signIn: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Sign up functionality
  const signUp = async (email: string, password: string, confirmPassword: string, displayName: string) => {
    try {
      if (password !== confirmPassword) {
        setLastError("Passwords do not match");
        toast({
          variant: "destructive",
          title: "Sign Up Failed",
          description: "Passwords do not match",
        });
        return;
      }
      
      setIsLoading(true);
      setLastOperation("signUp");
      setLastError(null);
      setAuthStatus("registering");
      addLog(`Sign up attempt for email: ${email}`, "info");
      
      const clearTimeout = setOperationTimeout("signUp", AUTH_OPERATION_TIMEOUT, () => {
        setIsLoading(false);
        setAuthStatus("timeout");
        setLastError("Sign up operation timed out");
        addLog("Sign up timed out", "error");
        toast({
          variant: "destructive",
          title: "Sign Up Failed",
          description: "The operation timed out. Please try again.",
        });
      });

      // Get environment-aware redirect URL and log it
      const redirectUrl = getEmailRedirectUrl('signup');
      addLog(`Sign up with environment-aware redirect URL: ${redirectUrl}`, "info");
      logEnvironmentInfo(); // Log current environment detection
      
      const finalDisplayName = displayName.trim();
      addLog(`Using display name: ${finalDisplayName}`, "info");

      // Check if the email appears to be an auto-generated test email
      const isTestEmail = email.startsWith('test-') && email.includes('@example.com');
      const defaultRole = isTestEmail ? 'auto_mock' : 'vendor';
      
      const signUpOptions = {
        email,
        password,
        options: {
          data: {
            display_name: finalDisplayName,
            role: defaultRole
          },
          emailRedirectTo: redirectUrl
        }
      };

      const { data, error: signUpError } = await supabase.auth.signUp(signUpOptions);

      clearTimeout();

      if (signUpError) {
        addLog(`Signup error: ${signUpError.message}`, "error");
        setIsLoading(false);
        setAuthStatus("error");
        setLastError(signUpError.message);
        
        let errorMessage = signUpError.message;
        
        if (signUpError.message.includes("already registered")) {
          errorMessage = "This email is already registered. Would you like to sign in instead?";
        } else if (signUpError.message === "Error sending confirmation email") {
          errorMessage = "Unable to send confirmation email. If you're in testing mode, check the test email viewer below.";
        } else if (signUpError.message.includes("weak password")) {
          errorMessage = "Please choose a stronger password with a mix of letters, numbers, and special characters.";
        } else if (signUpError.message.includes("profiles") && signUpError.message.includes("does not exist")) {
          errorMessage = "There was an issue setting up your profile. Please try again or contact support.";
          
          if (data?.user) {
            try {
              await createOrUpdateProfile(data.user.id, {
                display_name: finalDisplayName,
                email: email
              });
            } catch (profileError) {
              addLog(`Manual profile creation failed: ${(profileError as Error).message}`, "error");
            }
          }
        }
        
        toast({
          variant: "destructive",
          title: "Sign Up Failed",
          description: errorMessage,
        });
        
        throw new Error(errorMessage);
      }

      addLog(`Signup successful, user data: ${data.user?.id || "no user data"}`, "success");

      if (data?.user) {
        try {
          await createOrUpdateProfile(data.user.id, {
            display_name: finalDisplayName,
            email: email
          });
        } catch (profileError) {
          addLog(`Backup profile creation failed: ${(profileError as Error).message}`, "error");
        }
      }

      toast({
        title: "Account Created",
        description: "Please check your email for the confirmation link. If you don't receive it, check your spam folder or contact support.",
      });
      
      setAuthStatus("registration_complete");
    } catch (error: any) {
      addLog(`Exception in signUp: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out functionality
  const signOut = async () => {
    try {
      setIsLoading(true);
      setLastOperation("signOut");
      setLastError(null);
      setAuthStatus("signing_out");
      addLog("Signing out...", "info");
      
      const clearTimeout = setOperationTimeout("signOut", AUTH_OPERATION_TIMEOUT, () => {
        setIsLoading(false);
        setAuthStatus("timeout");
        setLastError("Sign out operation timed out");
        addLog("Sign out timed out, forcing sign out", "error");
        
        // Force sign out by clearing all auth state
        setUser(null);
        setSession(null);
        setIsAdmin(false);
        setIsVendor(false);
        setIsVendorLoading(false);
        setDisplayName("");
        setAuthStatus("unauthenticated");
        
        // Force clear all local storage for extra safety
        if (typeof window !== 'undefined' && window.localStorage) {
          const keysToRemove = Object.keys(localStorage).filter(key => 
            key.startsWith('sb-') || key.includes('supabase')
          );
          
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
          });
        }
        
        navigate("/");
      });

      // First clear local state (for immediate UI feedback)
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsVendor(false);
      setIsVendorLoading(false);
      setDisplayName("");
      
      // Clear local storage auth data for extra safety
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || key.includes('supabase')
        );
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
      }
      
      // Then call the API
      const { error } = await supabase.auth.signOut();
      
      clearTimeout();
      
      if (error) {
        addLog(`Sign out API call error (proceeding anyway): ${error.message}`, "warning");
      }
      
      addLog("Signed out successfully", "success");
      setAuthStatus("unauthenticated");
      
      toast({
        title: "Signed Out",
        description: "You have been signed out of your account.",
      });
      
      // Redirect to home page
      navigate("/");
    } catch (error) {
      addLog(`Exception in signOut: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
      
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: (error as Error).message || "An unexpected error occurred",
      });
      
      // Force clear everything on error
      if (typeof window !== 'undefined') {
        localStorage.clear();
        window.location.href = "/";
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Password reset functionality
  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      setLastOperation("resetPassword");
      setLastError(null);
      setAuthStatus("resetting_password");
      addLog(`Password reset requested for email: ${email}`, "info");
      
      const clearTimeout = setOperationTimeout("resetPassword", AUTH_OPERATION_TIMEOUT, () => {
        setIsLoading(false);
        setAuthStatus("timeout");
        setLastError("Password reset operation timed out");
        addLog("Password reset timed out", "error");
        toast({
          variant: "destructive",
          title: "Password Reset Failed",
          description: "The operation timed out. Please try again.",
        });
      });

      const resetOptions = {
        redirectTo: `${window.location.origin}/verify?type=recovery`
      };

      const { error } = await supabase.auth.resetPasswordForEmail(
        email, 
        resetOptions
      );

      clearTimeout();

      if (error) {
        setLastError(error.message);
        setAuthStatus("error");
        addLog(`Password reset error: ${error.message}`, "error");
        toast({
          variant: "destructive",
          title: "Password Reset Failed",
          description: error.message,
        });
        throw error;
      }

      addLog("Password reset email sent successfully", "success");
      setAuthStatus("password_reset_sent");
      
      toast({
        title: "Reset Link Sent",
        description: "Check your email for the password reset link.",
      });
    } catch (error) {
      addLog(`Exception in resetPassword: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Update password functionality
  const updatePassword = async (password: string) => {
    try {
      if (!password || password.length < 6) {
        const errorMsg = "Password must be at least 6 characters long.";
        setLastError(errorMsg);
        toast({
          variant: "destructive",
          title: "Password Update Failed",
          description: errorMsg,
        });
        return;
      }

      setIsLoading(true);
      setLastOperation("updatePassword");
      setLastError(null);
      setAuthStatus("updating_password");
      addLog("Updating password", "info");
      
      const clearTimeout = setOperationTimeout("updatePassword", AUTH_OPERATION_TIMEOUT, () => {
        setIsLoading(false);
        setAuthStatus("timeout");
        setLastError("Password update operation timed out");
        addLog("Password update timed out", "error");
        toast({
          variant: "destructive",
          title: "Password Update Failed",
          description: "The operation timed out. Please try again.",
        });
      });

      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      clearTimeout();

      if (error) {
        setLastError(error.message);
        setAuthStatus("error");
        addLog(`Password update error: ${error.message}`, "error");
        toast({
          variant: "destructive",
          title: "Password Update Failed",
          description: error.message,
        });
        throw error;
      }

      addLog("Password updated successfully", "success");
      setAuthStatus("password_updated");
      
      toast({
        title: "Password Updated",
        description: "Your password has been successfully reset.",
      });
    } catch (error) {
      addLog(`Exception in updatePassword: ${(error as Error).message}`, "error");
      setLastError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Derived state
  const isAuthenticated = !!session && !!user;

  // Context value
  const contextValue: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated,
    isAdmin,
    isVendor,
    isVendorLoading,
    displayName,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    createOrUpdateProfile,
    authStatus,
    lastError,
    lastOperation,
    debugLogs
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
